import React from 'react';
import { useIntl } from 'react-intl';
import { ReactSVG } from 'react-svg';
import classnames from 'classnames';
import Chip from '../../@vuexy/chips/ChipComponent';
import { getLocalizedName } from '../../../utility/util/util.service';

// Relation fields arrive either as an id string or as a hydrated { id } object.
const relationId = (relation) =>
  !relation ? null : typeof relation === 'string' ? relation : relation.id;

export const getAllergenIconSrc = (allergen) =>
  `/assets/icons/allergens/${allergen?.iconName}`;

/**
 * The allergens an addition carries, for display.
 *
 * Resolved from `foodItemAdditions` because the addition embedded in a group
 * entry is trimmed by the API and carries no allergens.
 */
export const getAdditionAllergens = (addition, additionsById, allergensById) => {
  const ids = additionsById.get(addition?.id)?.allergens;
  if (!ids?.length) return [];
  return ids
    .map((id) => allergensById.get(relationId(id)))
    .filter((allergen) => !!allergen);
};

// Item-level chips are full size; addition lines are dense, so theirs are scaled
// down to 65% (`allergen-chips-compact` in index.scss) to fit.
export const COMPACT_ICON_SIZE = 15.6;
export const DEFAULT_ICON_SIZE = 24;

/**
 * Icon + label chips for an addition's allergens. Mirrors the chip markup used
 * at item level (`item-data-info.component.js`) so allergens read identically
 * wherever a guest meets them.
 */
const AllergenChips = ({ allergens, className, compact = false }) => {
  const intl = useIntl();

  if (!allergens?.length) return null;

  const iconSize = compact ? COMPACT_ICON_SIZE : DEFAULT_ICON_SIZE;

  return (
    <div
      className={classnames(
        'd-flex flex-wrap align-items-center',
        { 'allergen-chips-compact': compact },
        className,
      )}
    >
      {allergens.map((allergen, index) => (
        <div key={allergen.id} className="custom-chip-wrapper">
          <Chip
            className={classnames({ 'mr-50': index < allergens.length - 1 })}
            avatarIcon={
              <ReactSVG
                className="allergen-icon"
                style={{ width: `${iconSize}px`, height: `${iconSize}px` }}
                src={getAllergenIconSrc(allergen)}
                beforeInjection={(svg) => {
                  svg.removeAttribute('width');
                  svg.removeAttribute('height');
                }}
              />
            }
            text={getLocalizedName(allergen, intl.locale)}
          />
        </div>
      ))}
    </div>
  );
};

export default AllergenChips;
